import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Brain, Banknote, Lightbulb, Users, ArrowRight, MapPin } from "lucide-react";

export default function PublicStats() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/public/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-paper">
      <div className="h-1.5 gradient-stripe" />
      <header className="bg-white/80 backdrop-blur-xl border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#1E3A8A] flex items-center justify-center text-white font-heading font-bold">PS</div>
            <div>
              <div className="font-heading font-bold text-xl">padaskillinfo</div>
              <div className="text-xs text-gray-500 -mt-0.5">Public Census Stats</div>
            </div>
          </Link>
          <Link to="/register"><Button className="bg-[#F59E0B] hover:bg-[#F59E0B]/90 text-white rounded-full">Register Now <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="text-center mb-12 animate-fade-up">
          <div className="text-sm font-semibold text-[#E11D48] uppercase tracking-widest mb-2">Live · Pithapuram</div>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight">
            <span className="text-[#1E3A8A]">{stats?.total?.toLocaleString() || "—"}</span> youth.<br />
            One bright future.
          </h1>
          <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
            Real-time, anonymized snapshot of the skilling census across Pithapuram constituency.
          </p>
        </div>

        {stats && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
              <BigStat color="#1E3A8A" icon={<Users />} value={stats.total} label="Registered" sub="Total youth" />
              <BigStat color="#F59E0B" icon={<Brain />} value={stats.ai_upskill_count} label="AI Track" sub="Interested" />
              <BigStat color="#E11D48" icon={<Lightbulb />} value={stats.startup_count} label="Startup-ready" sub="With ideas" />
              <BigStat color="#10B981" icon={<Banknote />} value={stats.bank_linkage_count} label="Bank Linkage" sub="Need support" />
            </div>

            <div className="grid lg:grid-cols-2 gap-6 mb-10">
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <h3 className="font-heading font-bold text-xl mb-4 flex items-center gap-2">
                  <Brain className="w-5 h-5 text-[#F59E0B]" /> Top interested domains
                </h3>
                <div className="space-y-3">
                  {stats.by_domain.map((d, i) => {
                    const max = Math.max(...stats.by_domain.map((x) => x.count));
                    const pct = (d.count / max) * 100;
                    return (
                      <div key={d.name}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="font-medium">{d.name}</span>
                          <span className="text-gray-500">{d.count}</span>
                        </div>
                        <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${pct}%`, background: ["#1E3A8A","#F59E0B","#E11D48","#10B981","#0EA5E9","#8B5CF6"][i % 6] }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <h3 className="font-heading font-bold text-xl mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[#E11D48]" /> Top villages
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {stats.by_village.map((v, i) => (
                    <div key={v.name} className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-3 border border-gray-100">
                      <div className="text-xs text-gray-500">#{i + 1}</div>
                      <div className="font-medium text-sm">{v.name}</div>
                      <div className="font-heading font-bold text-2xl text-[#1E3A8A]">{v.count}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-[#1E3A8A] rounded-3xl p-10 text-center text-white">
              <h2 className="font-heading font-bold text-3xl mb-3">Be part of the movement.</h2>
              <p className="opacity-90 max-w-xl mx-auto mb-6">Register your details and unlock free AI training, placement support and bank linkages through RTIH Pithapuram.</p>
              <Link to="/register"><Button className="bg-[#F59E0B] hover:bg-[#F59E0B]/90 text-white h-12 px-8 rounded-full">
                Register your details <ArrowRight className="w-4 h-4 ml-2" />
              </Button></Link>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function BigStat({ color, icon, value, label, sub }) {
  return (
    <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-[0_2px_10px_rgba(0,0,0,0.04)] hover:shadow-xl transition-all" data-testid={`public-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white mb-3" style={{ backgroundColor: color }}>{icon}</div>
      <div className="font-heading font-bold text-4xl tracking-tighter">{value?.toLocaleString()}</div>
      <div className="font-heading font-semibold mt-1">{label}</div>
      <div className="text-xs text-gray-500">{sub}</div>
    </div>
  );
}
